import { usePurchaseRequestDetailStore } from '../../../store/PurchaseRequestStore';
import { useFetchData } from '../../../hooks';
import { PurchaseRequestDetails } from '../../../types/PurchaseRequest';
import StatusTag from '../../../components/ui/StatusTag';
import { Table, TableColumnsType } from 'antd';
import ErrorModal from '../../../components/ui/modal/ErrorModal';
import { PageableResponse } from '../../../types';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { dateFormat } from '../../../lib/utils';
import { Resource } from '../../../lib/enums';

const PurchaseRequestDetailTable = () => {
  const { setData, resetSelectedRowKeys } = usePurchaseRequestDetailStore();

  const [searchParams] = useSearchParams();
  const startDate =
    searchParams.get('from') !== undefined && searchParams.get('from') !== null
      ? searchParams.get('from')
      : dayjs().subtract(6, 'month').format(dateFormat);
  const endDate =
    searchParams.get('to') !== undefined && searchParams.get('to') !== null
      ? searchParams.get('to')
      : dayjs().add(2, 'month').format(dateFormat);

  const {
    data: prDetails,
    isLoading,
    error,
    isError,
  } = useFetchData<PageableResponse<PurchaseRequestDetails>>(
    `/pr/requests/details`,
    {
      resource: searchParams.get('resource') || Resource.FULL,
      search: searchParams.get('search') || '',
      sku: searchParams.get('sku') || '',
      proder: searchParams.get('proder') || '',
      product: searchParams.get('product') || '',
      destination: searchParams.get('destination'),
      from: startDate,
      to: endDate,
      length: parseInt(searchParams.get('length') || '999999'),
    },
  );

  const columns: TableColumnsType<PurchaseRequestDetails> = [
    {
      title: 'Code',
      dataIndex: 'prNo',
    },
    {
      title: 'Product Code',
      dataIndex: 'productCode',
    },
    {
      title: 'Product',
      dataIndex: 'productName',
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'UoM',
      dataIndex: 'productUom',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
    },
    {
      title: 'Proder',
      dataIndex: 'proder',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
    },
    {
      title: 'Quantity PO',
      dataIndex: 'quantityPO',
    },
    {
      title: 'Quantity Received',
      dataIndex: 'quantityReceived',
    },
    {
      title: 'Required Date',
      dataIndex: 'requiredDate',
      sorter: (a: PurchaseRequestDetails, b: PurchaseRequestDetails) => {
        const updatedByComparison = a.updatedBy.localeCompare(b.updatedBy);
        return updatedByComparison !== 0
          ? updatedByComparison
          : (new Date(a.requiredDate || 0).getTime() - new Date(b.requiredDate || 0).getTime());
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: any) => {
        return <StatusTag status={status} />;
      },
    },
  ];
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setData(newSelectedRowKeys);
  };

  useEffect(() => {
    resetSelectedRowKeys();
  }, [resetSelectedRowKeys]);

  const rowSelection = {
    selectedRowKeys: usePurchaseRequestDetailStore((state) => state.data),
    onChange: onSelectChange,
  };

  const totalQuantity = prDetails?.data
    ?.filter(item => rowSelection.selectedRowKeys.includes(item.id))
    .reduce((total, item) => total + item.quantity, 0) || 0;

  useEffect(() => {
    setIsErrorModalOpen(isError);
  }, [isError]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  return (
    <>
      <Table
        rowKey={(record: PurchaseRequestDetails) => record.id}
        rowSelection={rowSelection}
        columns={columns}
        loading={isLoading}
        dataSource={prDetails?.data}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{
          y: 550,
        }}
        footer={() => (
          <div style={{ textAlign: 'right', paddingLeft: '10px' }}>
            <strong>Total Quantity: </strong> {totalQuantity}
          </div>
        )}
      />

      <ErrorModal
        visible={isErrorModalOpen}
        message={error?.message}
        onClose={handleErrorModalClose}
      />
    </>
  );
};

export default PurchaseRequestDetailTable;
